import {
  IMAGE_MIME_TYPES,
  MAX_GEOTAGGED_FILE_SIZE_MB,
  COMPRESSION_QUALITY
} from '../geotagging/index.constants';
import type { CompressImageOptions } from './index.types';
import { logger } from '../../logger';

/**
 * Load image from URL asynchronously
 * For blob URLs (from File objects), crossOrigin is not needed
 * For external URLs, crossOrigin = 'anonymous' is required to allow the image to be drawn on canvas
 */
const loadImage = async (
  url: string,
  useCors: boolean = false
): Promise<HTMLImageElement> => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Compress image to stay under size limit while maintaining quality
 * Uses progressive compression: reduce dimensions by 5% each iteration until under limit
 * Outputs WebP format with quality settings for better compression
 * Automatically extracts image dimensions and file size from the blob
 *
 * @param blob - The image blob to compress
 * @param options - Compression options
 * @returns Compressed blob in WebP format
 */
export const compressImage = async (
  blob: Blob,
  options?: CompressImageOptions
): Promise<Blob> => {
  const maxSizeInMB = options?.maxSizeInMB ?? MAX_GEOTAGGED_FILE_SIZE_MB;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  const minDimensionRatio = 0.5; // Minimum 50% of smallest dimension
  const compressionStep = 0.05; // Reduce by 5% each iteration
  const originalFileSize = blob.size;

  // If already under limit, return as is
  if (blob.size <= maxSizeInBytes) {
    return blob;
  }

  // Load the image to get its dimensions
  const imageUrl = URL.createObjectURL(blob);
  const img = await loadImage(imageUrl, false);

  // Extract dimensions from the loaded image
  const originalWidth = img.naturalWidth || img.width;
  const originalHeight = img.naturalHeight || img.height;
  const minDimension = Math.floor(
    Math.min(originalWidth, originalHeight) * minDimensionRatio
  );

  let currentWidth = originalWidth;
  let currentHeight = originalHeight;
  let compressedBlob: Blob = blob;
  const maxIterations = 20; // Safety limit

  // Progressive compression: reduce dimensions until under limit
  let iterations = 0;

  while (
    compressedBlob.size > maxSizeInBytes &&
    currentWidth > minDimension &&
    iterations < maxIterations
  ) {
    iterations++;

    // Reduce dimensions by 5%
    currentWidth = Math.max(
      minDimension,
      Math.floor(currentWidth * (1 - compressionStep))
    );
    currentHeight = Math.max(
      minDimension,
      Math.floor(currentHeight * (1 - compressionStep))
    );

    const canvas = document.createElement('canvas');
    canvas.width = currentWidth;
    canvas.height = currentHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      URL.revokeObjectURL(imageUrl);
      throw new Error('Failed to get canvas context for compression');
    }

    // Use high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw image to canvas with new dimensions
    ctx.drawImage(img, 0, 0, currentWidth, currentHeight);

    // Convert to WebP format with quality setting for better compression
    const dataUrl = canvas.toDataURL(
      IMAGE_MIME_TYPES.WEBP,
      COMPRESSION_QUALITY
    );
    compressedBlob = await (await fetch(dataUrl)).blob();
  }

  URL.revokeObjectURL(imageUrl);

  // If still too large after all compression attempts, return the best we can do
  if (compressedBlob.size > maxSizeInBytes) {
    logger.warn({
      message: `Image compression: Final size ${(
        compressedBlob.size /
        1024 /
        1024
      ).toFixed(2)}MB still exceeds limit ${maxSizeInMB}MB. Original: ${(
        originalFileSize /
        1024 /
        1024
      ).toFixed(2)}MB, Compressed dimensions: ${currentWidth}x${currentHeight}`
    });
  }

  return compressedBlob;
};
